import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { supabase, supabaseUrl } from "@/lib/supabase";

// Import shared models from @tinyclub/shared - single source of truth
import type {
  UserProfile,
  Club,
  ClubMember,
  ClubRole,
  ClubPublicData,
  ClubInvitation,
  Event,
  EventAttendee,
  EventOccurrence,
  Post,
  PostMedia,
  Poll,
  PollOption,
  Comment,
  Topic,
  Subtopic,
  UserTopic,
  GeocodingFeature,
  Notification,
  NotificationType,
  UserBlock,
  ClubLink,
  ClubFile,
  ClubInfoData,
  ClubExportJob,
  ClubExportScope,
  ClubExportStatus,
} from "@tinyclub/shared/models";

import type {
  ClubPaymentSettingsFull,
  UserClubSubscription,
} from "./paymentModels";

// Import enums for use within this file
import { EventType, EventRepeatRule, RsvpState } from "@tinyclub/shared/models";

// Re-export types for consumers
export type {
  UserProfile,
  Club,
  ClubMember,
  ClubRole,
  ClubPublicData,
  ClubInvitation,
  Event,
  EventAttendee,
  EventOccurrence,
  Post,
  PostMedia,
  Poll,
  PollOption,
  Comment,
  Topic,
  Subtopic,
  UserTopic,
  Notification,
  NotificationType,
  UserBlock,
  ClubLink,
  ClubFile,
  ClubInfoData,
  ClubExportJob,
  ClubExportScope,
  ClubExportStatus,
};
export type { ClubPaymentSettingsFull, OneTimePurchase } from "./paymentModels";
export type { UserClubSubscription } from "@tinyclub/shared/models";

// Extended type for clubs with their subscription info
export interface SubscribedClub extends Club {
  subscription: import("@tinyclub/shared/models").UserClubSubscription;
}

// Featured club shape from get_featured_clubs RPC (matches mobile)
export interface FeaturedClub {
  club_id: string;
  club_name: string;
  location_name: string | null;
  location_object: unknown;
  primary_image_small: string | null;
  primary_image_medium: string | null;
  primary_image_full: string | null;
  member_count: number;
  private: boolean;
  invite_code: string | null;
  display_order: number;
}

// Recommended club shape from get_random_clubs_within_radius RPC (matches mobile)
export interface RecommendedClub {
  club_id: string;
  club_name: string;
  location_name: string | null;
  location_object: unknown;
  primary_image_small: string | null;
  primary_image_medium: string | null;
  primary_image_full: string | null;
  member_count: number;
  private: boolean;
  invite_code: string | null;
  distance_miles: number;
}

// Re-export enums
export { EventType, EventRepeatRule, RsvpState };

// Moderation report shape from the moderation_reports table
export interface ModerationReport {
  id: number;
  item_type: "post" | "comment" | "club" | "user";
  item_id: string;
  reporter_id: string;
  reason: string | null;
  processed: boolean;
  created_at: string;
  // Joined reporter profile (partial)
  reporter?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    username: string | null;
    avatar_small: string | null;
  };
}

export const clubsApi = createApi({
  reducerPath: "clubsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Club", "ClubMember", "ClubInvitation", "Post", "UserProfile", "Event", "EventOccurrence", "Comment", "Poll", "Topic", "Subtopic", "Notification", "ClubPaymentSettings", "ClubSubscription", "BannedWord", "ClubEmailQuota", "UserBlock", "ModerationReport", "ClubInfo", "ClubExportJob"],
  endpoints: (builder) => ({
    // Fetch all clubs (same pattern as mobile - RLS allows reading all clubs for discovery)
    // Client-side filtering by membership happens in components
    fetchClubs: builder.query<Club[], void>({
      async queryFn() {
        try {
          const { data, error } = await supabase
            .from("clubs")
            .select("*")
            .order("updated_at", { ascending: false });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data || [] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result
                .filter((club) => club.id)
                .map((club) => ({
                  type: "Club" as const,
                  id: club.id!,
                })),
              { type: "Club", id: "LIST" },
            ]
          : [{ type: "Club", id: "LIST" }],
    }),

    // Fetch featured clubs (matches mobile's getFeaturedClubs pattern)
    getFeaturedClubs: builder.query<
      FeaturedClub[],
      { latitude: number; longitude: number; userId: string; limit?: number | null }
    >({
      async queryFn({ latitude, longitude, userId, limit = null }) {
        try {
          const { data, error } = await supabase.rpc("get_featured_clubs", {
            user_lat: latitude,
            user_lon: longitude,
            user_id: userId,
            result_limit: limit,
          });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: (data || []) as FeaturedClub[] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: ["Club"],
    }),

    // Fetch recommended (nearby) clubs (matches mobile's getRecommendedClubs pattern)
    getRecommendedClubs: builder.query<
      RecommendedClub[],
      { latitude: number; longitude: number; radiusMiles?: number; limit?: number; userId: string }
    >({
      async queryFn({ latitude, longitude, radiusMiles = 50, limit = 5, userId }) {
        try {
          const { data, error } = await supabase.rpc("get_random_clubs_within_radius", {
            user_lat: latitude,
            user_lon: longitude,
            radius_miles: radiusMiles,
            result_limit: limit,
            user_id: userId,
          });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: (data || []) as RecommendedClub[] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: ["Club"],
    }),

    // Fetch all club memberships for the current user (RLS filters to user's own memberships)
    // Same pattern as mobile's fetchAllClubMembers
    fetchAllClubMembers: builder.query<ClubMember[], void>({
      async queryFn() {
        try {
          const { data, error } = await supabase
            .from("club_members")
            .select("*");

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data || [] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((member) => ({
                type: "ClubMember" as const,
                id: member.id,
              })),
              { type: "ClubMember", id: "LIST" },
            ]
          : [{ type: "ClubMember", id: "LIST" }],
    }),

    // Fetch a single club by ID
    getClub: builder.query<Club | null, string>({
      async queryFn(clubId) {
        try {
          const { data, error } = await supabase
            .from("clubs")
            .select("*")
            .eq("id", clubId)
            .single();

          if (error) {
            if (error.code === "PGRST116") {
              // No rows returned
              return { data: null };
            }
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, clubId) => [{ type: "Club", id: clubId }],
    }),

    // Check user's membership in a club
    checkClubMembership: builder.query<
      ClubMember | null,
      { clubId: string; userId: string }
    >({
      async queryFn({ clubId, userId }) {
        try {
          const { data, error } = await supabase
            .from("club_members")
            .select("*")
            .eq("club_id", clubId)
            .eq("user_id", userId)
            .single();

          if (error) {
            if (error.code === "PGRST116") {
              // No rows returned
              return { data: null };
            }
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, { clubId, userId }) => [
        { type: "ClubMember", id: `${clubId}-${userId}` },
      ],
    }),

    // Fetch club posts (matches mobile's fetchClubPosts pattern)
    fetchClubPosts: builder.query<
      { posts: Post[]; hasMore: boolean },
      { clubId: string; userId?: string | undefined; offset?: number; limit?: number }
    >({
      async queryFn({ clubId, userId, offset = 0, limit = 20 }) {
        try {
          // Build the query - matches mobile's pattern with polls
          let query = supabase
            .from("posts")
            .select(
              `
              *,
              post_media(*),
              post_likes!left(user_id),
              comment_count:comments(count),
              polls(*, poll_options(*), poll_votes!left(user_id, option_id))
            `
            )
            .eq("club_id", clubId)
            .eq("moderation_status", "approved")
            // Only count approved comments (matches mobile's filter)
            .or("moderation_status.eq.approved,moderation_status.is.null", {
              referencedTable: "comments",
            })
            .order("pinned", { ascending: false })
            .order("created_at", { ascending: false })
            .range(offset, offset + limit); // Fetch one extra to check hasMore

          // Filter post_likes and poll_votes by user if provided
          if (userId) {
            query = query
              .eq("post_likes.user_id", userId)
              .eq("polls.poll_votes.user_id", userId);
          }

          const { data, error } = await query;

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          const hasMore = (data?.length || 0) > limit;
          const posts = (data || []).slice(0, limit).map((post: any) => {
            // Transform poll data to include user's vote (matches mobile pattern)
            // polls.post_id has UNIQUE constraint, so PostgREST returns single object (not array)
            let poll = null;
            if (post.polls) {
              const rawPoll = post.polls;
              const userVote =
                rawPoll.poll_votes && rawPoll.poll_votes.length > 0
                  ? rawPoll.poll_votes[0]
                  : null;
              poll = {
                ...rawPoll,
                options: rawPoll.poll_options || [],
                user_vote_option_id: userVote?.option_id || null,
                poll_options: undefined,
                poll_votes: undefined,
              };
            }
            return {
              ...post,
              liked: post.post_likes && post.post_likes.length > 0,
              post_likes: undefined,
              comment_count: post.comment_count?.[0]?.count || 0,
              poll,
              polls: undefined, // Remove raw polls data
            };
          });

          return { data: { posts, hasMore } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (result, _error, { clubId }) =>
        result
          ? [
              ...result.posts.map((post) => ({
                type: "Post" as const,
                id: post.id,
              })),
              { type: "Post", id: `LIST-${clubId}` },
            ]
          : [{ type: "Post", id: `LIST-${clubId}` }],
    }),

    // Fetch public user profile (for post authors, viewing other users, etc.)
    // Supports includeTopics parameter to fetch user interests
    fetchUserProfile: builder.query<
      UserProfile | null,
      string | { id: string; includeTopics?: boolean }
    >({
      async queryFn(arg) {
        try {
          const userId = typeof arg === "string" ? arg : arg.id;
          const includeTopics = typeof arg === "object" ? arg.includeTopics : false;

          // Build select string - optionally include user_topics with nested relations
          const select = includeTopics
            ? "*,user_topics(id,topic_id,subtopic_id,topics(*,topic_categories(*)),subtopics(*,parent_topic:topics(*)))"
            : "*";

          const { data, error } = await supabase
            .from("user_profiles")
            .select(select)
            .eq("id", userId)
            .eq("is_active", true)
            .single();

          if (error) {
            if (error.code === "PGRST116") {
              return { data: null };
            }
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as UserProfile };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, arg) => [
        { type: "UserProfile", id: typeof arg === "string" ? arg : arg.id },
      ],
    }),

    // Fetch current user's profile (for their own profile page)
    // This fetches the full profile including all fields for the logged-in user
    // Supports includeTopics parameter to fetch user interests
    fetchCurrentUserProfile: builder.query<
      UserProfile | null,
      string | { id: string; includeTopics?: boolean }
    >({
      async queryFn(arg) {
        try {
          const userId = typeof arg === "string" ? arg : arg.id;
          const includeTopics = typeof arg === "object" ? arg.includeTopics : false;

          // Build select string - optionally include user_topics with nested relations
          const select = includeTopics
            ? "*,user_topics(id,topic_id,subtopic_id,topics(*,topic_categories(*)),subtopics(*,parent_topic:topics(*)))"
            : "*";

          const { data, error } = await supabase
            .from("user_profiles")
            .select(select)
            .eq("id", userId)
            .single();

          if (error) {
            if (error.code === "PGRST116") {
              return { data: null };
            }
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as UserProfile };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, arg) => {
        const userId = typeof arg === "string" ? arg : arg.id;
        return [{ type: "UserProfile", id: userId }];
      },
    }),

    // Fetch multiple user profiles by IDs (batch for efficiency)
    fetchUserProfilesByIds: builder.query<UserProfile[], string[]>({
      async queryFn(userIds) {
        try {
          if (userIds.length === 0) {
            return { data: [] };
          }

          const { data, error } = await supabase
            .from("user_profiles")
            .select("*")
            .in("id", userIds)
            .eq("is_active", true);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data || [] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((profile) => ({
                type: "UserProfile" as const,
                id: profile.id,
              })),
              { type: "UserProfile", id: "LIST" },
            ]
          : [{ type: "UserProfile", id: "LIST" }],
    }),

    // Update user profile
    // Use Record<string, unknown> to allow null values for clearing fields
    updateUserProfile: builder.mutation<
      UserProfile | null,
      { id: string } & Record<string, unknown>
    >({
      async queryFn(profile) {
        try {
          const { data, error } = await supabase
            .from("user_profiles")
            .update(profile)
            .eq("id", profile.id)
            .select("*")
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as UserProfile };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "UserProfile", id },
      ],
    }),

    // Fetch club members with user profiles (for displaying member info)
    fetchClubMembers: builder.query<
      (ClubMember & { user_profile?: UserProfile })[],
      { clubId: string; includeUserProfiles?: boolean }
    >({
      async queryFn({ clubId, includeUserProfiles = false }) {
        try {
          // Use separate queries depending on whether we need profiles
          // This avoids TypeScript issues with dynamic select strings
          if (includeUserProfiles) {
            const { data, error } = await supabase
              .from("club_members")
              .select("*, user_profile:user_profiles(*)")
              .eq("club_id", clubId);

            if (error) {
              return { error: { status: "CUSTOM_ERROR", error: error.message } };
            }

            return { data: (data || []) as (ClubMember & { user_profile?: UserProfile })[] };
          } else {
            const { data, error } = await supabase
              .from("club_members")
              .select("*")
              .eq("club_id", clubId);

            if (error) {
              return { error: { status: "CUSTOM_ERROR", error: error.message } };
            }

            return { data: (data || []) as (ClubMember & { user_profile?: UserProfile })[] };
          }
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, { clubId }) => [
        { type: "ClubMember", id: `LIST-${clubId}` },
      ],
    }),

    // Fetch club members who have available_irl = true (for Available IRL section)
    fetchAvailableClubMembers: builder.query<
      (ClubMember & { user_profile: UserProfile })[],
      { clubId: string }
    >({
      async queryFn({ clubId }) {
        try {
          const { data, error } = await supabase
            .from("club_members")
            .select("*, user_profile:user_profiles!inner(*)")
            .eq("club_id", clubId)
            .eq("user_profile.available_irl", true);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          // Filter to ensure user_profile exists and available_irl is true
          const filteredData = (data || []).filter(
            (member: any) => member.user_profile && member.user_profile.available_irl
          );

          return { data: filteredData as (ClubMember & { user_profile: UserProfile })[] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, { clubId }) => [
        { type: "ClubMember", id: `${clubId}-available` },
      ],
    }),

    // Search club members for mention autocomplete
    // Matches mobile's searchClubMembersForMention pattern
    searchClubMembersForMention: builder.query<
      Array<{
        user_id: string;
        username: string | null;
        first_name: string | null;
        last_name: string | null;
        display_name: string | null;
        avatar_small: string | null;
      }>,
      { clubId: string; query: string; currentUserId: string; postId?: string }
    >({
      async queryFn({ clubId, query, currentUserId, postId }) {
        try {
          const { data, error } = await supabase.rpc(
            "search_club_members_for_mention",
            {
              p_club_id: clubId,
              p_query: query,
              p_current_user_id: currentUserId,
              p_limit: 10,
              ...(postId && { p_post_id: postId }),
            }
          );

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data || [] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      // Short cache time for search results
      keepUnusedDataFor: 30,
    }),

    // Fetch discoverable clubs that a user belongs to (for profile display)
    // Matches mobile's fetchUserDiscoverableClubs pattern
    fetchUserDiscoverableClubs: builder.query<{ clubs: Club }[], string>({
      async queryFn(userId) {
        try {
          const { data, error } = await supabase
            .from("club_members")
            .select(
              "clubs!inner(id,name,location_name,location_object,primary_image_medium,member_count,invite_code,directory_enabled,moderation_status)"
            )
            .eq("user_id", userId)
            .eq("clubs.directory_enabled", true)
            .eq("clubs.moderation_status", "approved");

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: (data || []) as { clubs: Club }[] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, userId) => [
        { type: "ClubMember", id: `discoverable-${userId}` },
      ],
    }),

    // Fetch club events (matches mobile's fetchEvents pattern)
    // Uses event_occurrences table with embedded rootEvent
    fetchClubEvents: builder.query<
      EventOccurrence[],
      { clubId: string; includeAttendees?: boolean }
    >({
      async queryFn({ clubId, includeAttendees = true }) {
        try {
          const now = new Date().toISOString();

          // Build select string - always include rootEvent, optionally include attendees
          const select = includeAttendees
            ? "*, rootEvent:events!inner(*), event_attendees(*)"
            : "*, rootEvent:events!inner(*)";

          // Fetch events starting from beginning of today (to include
          // no-end-time events that started today) plus any still ongoing.
          const startOfToday = new Date(now);
          startOfToday.setHours(0, 0, 0, 0);
          const startOfTodayISO = startOfToday.toISOString();

          const { data, error } = await supabase
            .from("event_occurrences")
            .select(select)
            .eq("rootEvent.club_id", clubId)
            .eq("canceled", false)
            .or(`occurrence_start.gte.${startOfTodayISO},occurrence_end.gte.${now}`)
            .order("occurrence_start", { ascending: true });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          // Client-side filter: future, ongoing, or no-end-time events from today
          const nowDate = new Date(now);
          const occurrences: EventOccurrence[] = (data || [])
            .filter((item: any) => {
              const startTime = new Date(item.occurrence_start);
              if (startTime >= nowDate) return true;
              const endTime = item.occurrence_end ? new Date(item.occurrence_end) : null;
              if (endTime) return endTime > nowDate;
              // No end time: show until end of the day it started
              const endOfStartDay = new Date(startTime);
              endOfStartDay.setHours(23, 59, 59, 999);
              return endOfStartDay >= nowDate;
            })
            .map((item: any) => ({
              ...item,
              rootEvent: item.rootEvent,
              event_attendees: item.event_attendees || [],
            }));

          return { data: occurrences };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (result, _error, { clubId }) =>
        result
          ? [
              ...result.map((occurrence) => ({
                type: "EventOccurrence" as const,
                id: occurrence.id,
              })),
              { type: "EventOccurrence", id: `LIST-${clubId}` },
            ]
          : [{ type: "EventOccurrence", id: `LIST-${clubId}` }],
    }),

    // Fetch all events for the current user across all clubs
    // Uses RPC function same as mobile (get_user_event_occurrences_with_details)
    fetchAllEvents: builder.query<
      EventOccurrence[],
      { includeAttendees?: boolean; includeReactions?: boolean; includePast?: boolean; userId?: string }
    >({
      async queryFn({ includeAttendees = true, includeReactions = false, includePast = false, userId }) {
        try {
          // Use RPC function to get events (same as mobile)
          const { data, error } = await supabase.rpc(
            "get_user_event_occurrences_with_details",
            {
              p_user_id: userId,
              p_include_attendees: includeAttendees,
              p_include_reactions: includeReactions,
            }
          );

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          const now = new Date().toISOString();

          const occurrences: EventOccurrence[] = (data || [])
            .filter((item: any) => {
              if (item.canceled) return false;
              // When includePast is true, keep all non-canceled events
              if (includePast) return true;
              // Filter to future or still-ongoing events
              const nowDate = new Date(now);
              const startTime = new Date(item.occurrence_start);
              if (startTime >= nowDate) return true;
              const endTime = item.occurrence_end ? new Date(item.occurrence_end) : null;
              if (endTime) return endTime > nowDate;
              // No end time: show until end of the day it started
              const endOfStartDay = new Date(startTime);
              endOfStartDay.setHours(23, 59, 59, 999);
              return endOfStartDay >= nowDate;
            })
            .map((item: any) => ({
              ...item,
              rootEvent: item.root_event,
              event_attendees: includeAttendees ? (item.attendees || []) : [],
              event_reactions: includeReactions ? (item.reactions || []) : [],
            }))
            .sort(
              (a: EventOccurrence, b: EventOccurrence) =>
                new Date(a.occurrence_start).getTime() -
                new Date(b.occurrence_start).getTime()
            );

          return { data: occurrences };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((occurrence) => ({
                type: "EventOccurrence" as const,
                id: occurrence.id,
              })),
              { type: "EventOccurrence", id: "LIST_ALL" },
            ]
          : [{ type: "EventOccurrence", id: "LIST_ALL" }],
    }),

    // Fetch a single post by ID (matches mobile's fetchPost)
    fetchPost: builder.query<Post | null, { postId: string; userId?: string }>({
      async queryFn({ postId, userId }) {
        try {
          // Build the query - include post_media, comment count, and polls (matches mobile)
          let query = supabase
            .from("posts")
            .select(`
              *,
              post_media(*),
              post_likes!left(user_id),
              comment_count:comments(count),
              polls(*, poll_options(*), poll_votes!left(user_id, option_id))
            `)
            .eq("id", postId)
            .eq("moderation_status", "approved")
            // Only count approved comments (matches mobile's filter)
            .or("moderation_status.eq.approved,moderation_status.is.null", {
              referencedTable: "comments",
            });

          // Filter post_likes and poll_votes by user if provided
          if (userId) {
            query = query
              .eq("post_likes.user_id", userId)
              .eq("polls.poll_votes.user_id", userId);
          }

          const { data, error } = await query.single();

          if (error) {
            if (error.code === "PGRST116") {
              // Not found
              return { data: null };
            }
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          if (!data) {
            return { data: null };
          }

          // Transform poll data to include user's vote (matches mobile pattern)
          let poll = null;
          const rawPoll = (data as any).polls;
          if (rawPoll && rawPoll.id) {
            const userVote =
              rawPoll.poll_votes && rawPoll.poll_votes.length > 0
                ? rawPoll.poll_votes[0]
                : null;
            poll = {
              ...rawPoll,
              options: rawPoll.poll_options || [],
              user_vote_option_id: userVote?.option_id || null,
              poll_options: undefined,
              poll_votes: undefined,
            };
          }

          // Extract comment count from the nested count query
          const commentCount =
            Array.isArray(data.comment_count) && data.comment_count[0]
              ? data.comment_count[0].count
              : 0;

          return {
            data: {
              ...data,
              post_media: data.post_media || [],
              comment_count: commentCount,
              liked: (data as any).post_likes && (data as any).post_likes.length > 0,
              post_likes: undefined,
              poll,
              polls: undefined, // Remove raw polls data
            } as Post,
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, { postId }) => [{ type: "Post", id: postId }],
    }),

    // Fetch comments for a post (matches mobile's fetchPostComments)
    fetchPostComments: builder.query<Comment[], { postId: string; userId?: string }>({
      async queryFn({ postId, userId }) {
        try {
          const { data, error } = await supabase
            .from("comments")
            .select("*")
            .eq("post_id", postId)
            .eq("deleted_by_user", false)
            .eq("moderated_deleted", false)
            .eq("moderation_status", "approved") // Match mobile's moderation filter
            .order("created_at", { ascending: true });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          if (!data) {
            return { data: [] };
          }

          // Check which comments the user has liked
          let likedCommentIds: Set<string> = new Set();
          if (userId && data.length > 0) {
            const commentIds = data.map((c) => c.id);
            const { data: likesData } = await supabase
              .from("comment_likes")
              .select("comment_id")
              .eq("user_id", userId)
              .in("comment_id", commentIds);
            if (likesData) {
              likedCommentIds = new Set(likesData.map((l) => l.comment_id));
            }
          }

          return {
            data: data.map((comment) => ({
              ...comment,
              liked: likedCommentIds.has(comment.id),
            })) as Comment[],
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (result, _error, { postId }) =>
        result
          ? [
              ...result.map((c) => ({ type: "Comment" as const, id: c.id })),
              { type: "Comment", id: `LIST-${postId}` },
            ]
          : [{ type: "Comment", id: `LIST-${postId}` }],
    }),

    // Create a new comment
    createComment: builder.mutation<
      Comment,
      { post_id: string; user_id: string; body_text: string }
    >({
      async queryFn(newComment) {
        try {
          const { data, error } = await supabase
            .from("comments")
            .insert({
              post_id: newComment.post_id,
              user_id: newComment.user_id,
              body_text: newComment.body_text,
              like_count: 0,
              deleted_by_user: false,
              moderated_deleted: false,
            })
            .select("*")
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as Comment };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { post_id }) => [
        { type: "Comment", id: `LIST-${post_id}` },
        { type: "Post", id: post_id },
      ],
    }),

    // Create a new post (matches mobile's createPost pattern)
    createPost: builder.mutation<
      Post,
      Omit<Post, "id" | "created_at" | "updated_at" | "post_media" | "liked"> & {
        // Optional video processing fields (set when uploading video)
        is_processing?: boolean;
        processing_status?: string;
      }
    >({
      async queryFn(newPost) {
        try {
          const { data, error } = await supabase
            .from("posts")
            .insert(newPost)
            .select("*, post_media(*)")
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          if (!data) {
            return {
              error: {
                status: "CUSTOM_ERROR",
                error: "Failed to create post - no data returned",
              },
            };
          }

          return {
            data: {
              ...data,
              post_media: data.post_media || [],
              liked: false,
            } as Post,
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, args) => [
        { type: "Post", id: "LIST" },
        { type: "Post", id: `LIST-${args.club_id}` },
      ],
    }),

    // RSVP to an event (upsert pattern - matches mobile's createEventAttendee)
    createEventAttendee: builder.mutation<
      EventAttendee,
      { occurrenceId: string; userId: string; rsvpStatus: RsvpState }
    >({
      async queryFn({ occurrenceId, userId, rsvpStatus }) {
        try {
          // Use upsert with on_conflict to handle both create and update
          const { data, error } = await supabase
            .from("event_attendees")
            .upsert(
              {
                occurrence_id: occurrenceId,
                user_id: userId,
                rsvp_status: rsvpStatus,
              },
              { onConflict: "occurrence_id,user_id" }
            )
            .select("*")
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as EventAttendee };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { occurrenceId }) => [
        { type: "EventOccurrence", id: occurrenceId },
        { type: "EventOccurrence", id: "LIST_ALL" },
      ],
    }),

    // Update RSVP status for an event (matches mobile's updateEventAttendee)
    updateEventAttendee: builder.mutation<
      EventAttendee,
      { attendeeId: string; rsvpStatus: RsvpState; occurrenceId: string }
    >({
      async queryFn({ attendeeId, rsvpStatus }) {
        try {
          const { data, error } = await supabase
            .from("event_attendees")
            .update({ rsvp_status: rsvpStatus })
            .eq("id", attendeeId)
            .select("*")
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as EventAttendee };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { occurrenceId }) => [
        { type: "EventOccurrence", id: occurrenceId },
        { type: "EventOccurrence", id: "LIST_ALL" },
      ],
    }),

    // Remove RSVP from an event (matches mobile's deleteEventAttendee)
    deleteEventAttendee: builder.mutation<void, { attendeeId: string; occurrenceId: string }>({
      async queryFn({ attendeeId }) {
        try {
          const { error } = await supabase
            .from("event_attendees")
            .delete()
            .eq("id", attendeeId);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { occurrenceId }) => [
        { type: "EventOccurrence", id: occurrenceId },
        { type: "EventOccurrence", id: "LIST_ALL" },
      ],
    }),

    // Update an existing club (matches mobile's updateClub pattern)
    updateClub: builder.mutation<Club | null, Partial<Club> & { id: string }>({
      async queryFn(club) {
        try {
          const { data, error } = await supabase
            .from("clubs")
            .update(club)
            .eq("id", club.id)
            .select("*")
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as Club };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: "Club", id }],
    }),

    // Delete a club (matches mobile's deleteClub pattern)
    deleteClub: builder.mutation<{ id: string; cleanupSummary?: any }, string>({
      async queryFn(clubId) {
        try {
          // 1. Call cleanup edge function first (handles payment/subscription cleanup)
          // Blocking (matches mobile): if cleanup fails we must NOT delete the club —
          // the cascade would wipe club_subscriptions while Stripe keeps charging members.
          const { data, error } = await supabase.functions.invoke(
            "cleanup-club-payments",
            { body: { clubId } }
          );

          if (error) {
            // For non-2xx responses, the server's error message is in the response body
            let message = error.message;
            try {
              const body = await (error as { context?: Response }).context?.json();
              if (body?.error) message = body.error;
            } catch {
              // fall back to the generic invoke error message
            }
            throw new Error(`Payment cleanup failed: ${message}`);
          }

          if (!data?.success) {
            throw new Error(`Payment cleanup failed: ${data?.error || "Unknown error"}`);
          }

          const cleanupSummary = data.summary;
          console.log("[deleteClub] Cleanup summary:", cleanupSummary);

          // 2. Delete club from database
          const { error: deleteError } = await supabase
            .from("clubs")
            .delete()
            .eq("id", clubId);

          if (deleteError) {
            throw new Error("Club deletion failed");
          }

          // 3. Return success with cleanup details
          return {
            data: {
              id: clubId,
              cleanupSummary,
            },
          };
        } catch (error) {
          console.error("[deleteClub] Error:", error);
          return {
            error: {
              status: "CUSTOM_ERROR",
              error:
                error instanceof Error ? error.message : "Unknown error",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, id) => [
        { type: "Club", id },
        { type: "Club", id: "LIST" },
      ],
    }),

    // Create a new club
    createClub: builder.mutation<
      Club | null,
      {
        name: string;
        description?: string | undefined;
        creatorId: string;
        private?: boolean;
        post_permission?: "everyone" | "managers";
        hang_permission?: "everyone" | "managers";
        show_member_count?: boolean;
        directory_enabled?: boolean;
        location_object?: GeocodingFeature | null;
        location_name?: string | null;
        primary_image_small?: string;
        primary_image_medium?: string;
        primary_image_full?: string;
      }
    >({
      async queryFn({
        name,
        description,
        creatorId,
        private: isPrivate = false,
        post_permission = "everyone",
        hang_permission = "everyone",
        show_member_count = true,
        directory_enabled = true,
        location_object,
        location_name,
        primary_image_small,
        primary_image_medium,
        primary_image_full,
      }) {
        try {
          // Create the club
          const { data: clubData, error: clubError } = await supabase
            .from("clubs")
            .insert({
              name,
              description: description || null,
              creator_id: creatorId,
              private: isPrivate,
              post_permission,
              hang_permission,
              directory_enabled,
              show_member_count,
              location_object: location_object || null,
              location_name: location_name || null,
              ...(primary_image_small && { primary_image_small }),
              ...(primary_image_medium && { primary_image_medium }),
              ...(primary_image_full && { primary_image_full }),
            })
            .select("*")
            .single();

          if (clubError) {
            return { error: { status: "CUSTOM_ERROR", error: clubError.message } };
          }

          if (!clubData) {
            return {
              error: { status: "CUSTOM_ERROR", error: "Failed to create club" },
            };
          }

          // Creator is automatically added as owner by the
          // add_creator_as_admin_trigger database trigger on clubs INSERT

          return { data: clubData as Club };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: [
        { type: "Club", id: "LIST" },
        { type: "ClubMember", id: "LIST" },
      ],
    }),

    // Create a new event (matches mobile's createEvent pattern)
    createEvent: builder.mutation<
      Event | null,
      {
        club_id: string;
        creator_id: string;
        title: string;
        event_type: EventType;
        description?: string;
        location_name?: string;
        location_object?: unknown;
        start_time?: string;
        end_time?: string;
        timezone?: string;
        is_all_day?: boolean;
        is_virtual?: boolean;
        virtual_link_url?: string;
        allows_public_link?: boolean;
        allows_quick_replies?: boolean;
        primary_image_small?: string;
        primary_image_medium?: string;
        primary_image_full?: string;
        repeat_rule?: EventRepeatRule | null;
      }
    >({
      async queryFn(newEvent) {
        try {
          const { data, error } = await supabase
            .from("events")
            .insert({
              club_id: newEvent.club_id,
              creator_id: newEvent.creator_id,
              title: newEvent.title,
              event_type: newEvent.event_type,
              description: newEvent.description || null,
              location_name: newEvent.location_name || null,
              location_object: newEvent.location_object || null,
              start_time: newEvent.start_time || null,
              end_time: newEvent.end_time || null,
              timezone: newEvent.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
              is_all_day: newEvent.is_all_day || false,
              is_virtual: newEvent.is_virtual || false,
              virtual_link_url: newEvent.virtual_link_url || null,
              allows_public_link: newEvent.allows_public_link ?? true,
              allows_quick_replies: newEvent.allows_quick_replies ?? true,
              ...(newEvent.primary_image_small && { primary_image_small: newEvent.primary_image_small }),
              ...(newEvent.primary_image_medium && { primary_image_medium: newEvent.primary_image_medium }),
              ...(newEvent.primary_image_full && { primary_image_full: newEvent.primary_image_full }),
              ...(newEvent.repeat_rule !== undefined && { repeat_rule: newEvent.repeat_rule }),
            })
            .select("*")
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as Event };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { club_id }) => [
        { type: "EventOccurrence", id: `LIST-${club_id}` },
        { type: "EventOccurrence", id: "LIST_ALL" },
      ],
    }),

    // Create an event occurrence (matches mobile's createEventOccurrence pattern)
    createEventOccurrence: builder.mutation<
      EventOccurrence | null,
      {
        event_id: string;
        occurrence_start: string;
        occurrence_end: string;
        club_id: string; // For cache invalidation
      }
    >({
      async queryFn({ event_id, occurrence_start, occurrence_end }) {
        try {
          // First create the occurrence
          const { data: occurrenceData, error: occurrenceError } = await supabase
            .from("event_occurrences")
            .insert({
              event_id,
              occurrence_start,
              occurrence_end,
              canceled: false,
            })
            .select("*, rootEvent:events!inner(*)")
            .single();

          if (occurrenceError) {
            return { error: { status: "CUSTOM_ERROR", error: occurrenceError.message } };
          }

          return {
            data: {
              ...occurrenceData,
              rootEvent: occurrenceData.rootEvent,
              event_attendees: [],
            } as EventOccurrence,
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { club_id }) => [
        { type: "EventOccurrence", id: `LIST-${club_id}` },
        { type: "EventOccurrence", id: "LIST_ALL" },
      ],
    }),

    // Update an existing event (matches mobile's updateEvent pattern)
    updateEvent: builder.mutation<
      Event | null,
      { id: string; club_id: string } & Partial<Omit<Event, "id" | "created_at" | "updated_at">>
    >({
      async queryFn({ id, ...updates }) {
        try {
          const { data, error } = await supabase
            .from("events")
            .update(updates)
            .eq("id", id)
            .select("*")
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as Event };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { id, club_id }) => [
        { type: "Event", id },
        { type: "EventOccurrence", id: `LIST-${club_id}` },
        { type: "EventOccurrence", id: "LIST_ALL" },
      ],
    }),

    // Delete an event (matches mobile's deleteEvent pattern)
    deleteEvent: builder.mutation<
      { success: boolean },
      { eventId: string; clubId: string }
    >({
      async queryFn({ eventId }) {
        try {
          const { error } = await supabase
            .from("events")
            .delete()
            .eq("id", eventId);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { eventId, clubId }) => [
        { type: "Event", id: eventId },
        { type: "EventOccurrence", id: `LIST-${clubId}` },
        { type: "EventOccurrence", id: "LIST_ALL" },
      ],
    }),

    // -----------------------------------------------------
    // Event Reaction Endpoints
    // -----------------------------------------------------

    createEventReaction: builder.mutation<
      any,
      { occurrenceId: string; reactionType: string }
    >({
      async queryFn({ occurrenceId, reactionType }) {
        try {
          const { data, error } = await supabase.rpc("create_event_reaction", {
            p_occurrence_id: occurrenceId,
            p_reaction_type: reactionType,
          });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { occurrenceId }) => [
        { type: "EventOccurrence", id: occurrenceId },
      ],
    }),

    deleteEventReaction: builder.mutation<
      boolean,
      { reactionId: string; occurrenceId: string }
    >({
      async queryFn({ reactionId }) {
        try {
          const { data, error } = await supabase.rpc("delete_event_reaction", {
            p_reaction_id: reactionId,
          });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: !!data };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { occurrenceId }) => [
        { type: "EventOccurrence", id: occurrenceId },
        { type: "EventOccurrence", id: "LIST_ALL" },
      ],
    }),

    // -----------------------------------------------------
    // Poll Endpoints
    // -----------------------------------------------------

    // Create a poll for a post (matches mobile's createPoll)
    createPoll: builder.mutation<
      Poll,
      { postId: string; durationHours: number; closesAt: string; options: string[]; clubId?: string }
    >({
      async queryFn({ postId, durationHours, closesAt, options }) {
        try {
          // Create the poll
          const { data: pollData, error: pollError } = await supabase
            .from("polls")
            .insert({
              post_id: postId,
              duration_hours: durationHours,
              closes_at: closesAt,
            })
            .select("*")
            .single();

          if (pollError) {
            return { error: { status: "CUSTOM_ERROR", error: pollError.message } };
          }

          if (!pollData) {
            return { error: { status: "CUSTOM_ERROR", error: "Failed to create poll" } };
          }

          // Create all poll options
          const pollOptions: PollOption[] = [];
          for (let i = 0; i < options.length; i++) {
            const { data: optionData, error: optionError } = await supabase
              .from("poll_options")
              .insert({
                poll_id: pollData.id,
                option_text: options[i],
                sort_order: i,
              })
              .select("*")
              .single();

            if (optionError) {
              return { error: { status: "CUSTOM_ERROR", error: optionError.message } };
            }

            if (optionData) {
              pollOptions.push(optionData as PollOption);
            }
          }

          return {
            data: {
              ...pollData,
              options: pollOptions,
              user_vote_option_id: null,
            } as Poll,
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { postId, clubId }) => [
        { type: "Post", id: postId },
        ...(clubId ? [{ type: "Post" as const, id: `LIST-${clubId}` }] : []),
      ],
    }),

    // Delete a poll from a post
    deletePoll: builder.mutation<
      { success: boolean },
      { pollId: string; postId: string; clubId?: string }
    >({
      async queryFn({ pollId }) {
        try {
          // Delete poll (poll_options and poll_votes will cascade delete via foreign keys)
          const { error } = await supabase.from("polls").delete().eq("id", pollId);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { postId, clubId }) => [
        { type: "Post", id: postId },
        { type: "Poll", id: postId },
        ...(clubId ? [{ type: "Post" as const, id: `LIST-${clubId}` }] : []),
      ],
    }),

    // Vote on a poll option (matches mobile's votePoll with optimistic updates)
    votePoll: builder.mutation<
      { success: boolean },
      {
        pollId: string;
        optionId: string;
        postId: string;
        clubId?: string;
        previousOptionId?: string | null;
        userId: string;
      }
    >({
      async queryFn({ pollId, optionId, previousOptionId, userId }) {
        try {
          // If user already voted, delete old vote first (to change vote)
          if (previousOptionId) {
            await supabase
              .from("poll_votes")
              .delete()
              .eq("poll_id", pollId)
              .eq("user_id", userId);
            // Ignore delete errors - might not have voted yet
          }

          // Insert new vote
          const { error } = await supabase.from("poll_votes").insert({
            poll_id: pollId,
            option_id: optionId,
            user_id: userId,
          });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      async onQueryStarted(
        { optionId, postId, clubId, previousOptionId, userId },
        { dispatch, getState, queryFulfilled }
      ) {
        // Helper to optimistically update a poll within a post (matches mobile pattern)
        const updatePollInPost = (post: Post | null | undefined) => {
          if (!post?.poll) return;
          // Update vote counts (only if actually changing options)
          if (previousOptionId && previousOptionId !== optionId) {
            const oldOption = post.poll.options.find((o) => o.id === previousOptionId);
            if (oldOption) {
              oldOption.vote_count = Math.max(0, (oldOption.vote_count || 1) - 1);
            }
          }
          if (previousOptionId !== optionId) {
            const newOption = post.poll.options.find((o) => o.id === optionId);
            if (newOption) {
              newOption.vote_count = (newOption.vote_count || 0) + 1;
            }
          }
          // Update total votes (only increment if first vote, not changing)
          if (!previousOptionId) {
            post.poll.total_votes = (post.poll.total_votes || 0) + 1;
          }
          // Update user's vote selection
          post.poll.user_vote_option_id = optionId;
        };

        // Patch fetchPost cache for the detail view
        const patchPost = dispatch(
          clubsApi.util.updateQueryData(
            "fetchPost",
            { postId, userId },
            (draft) => {
              updatePollInPost(draft);
            }
          )
        );

        // Patch all fetchClubPosts cache entries that contain this post
        // (may have multiple entries with different offset/limit args)
        const listPatches: Array<{ undo: () => void }> = [];
        if (clubId) {
          const state = getState() as any;
          const queries = state.clubsApi?.queries ?? {};
          for (const entry of Object.values(queries)) {
            const q = entry as any;
            if (
              q?.endpointName === "fetchClubPosts" &&
              q?.originalArgs?.clubId === clubId &&
              q?.data
            ) {
              const patch = dispatch(
                clubsApi.util.updateQueryData(
                  "fetchClubPosts",
                  q.originalArgs,
                  (draft) => {
                    const post = draft.posts.find((p) => p.id === postId);
                    updatePollInPost(post);
                  }
                )
              );
              listPatches.push(patch);
            }
          }
        }

        try {
          await queryFulfilled;
        } catch {
          // Revert optimistic updates on failure
          patchPost.undo();
          listPatches.forEach((p) => p.undo());
        }
      },
      invalidatesTags: (_result, _error, { postId, clubId }) => [
        { type: "Post", id: postId },
        { type: "Poll", id: postId },
        ...(clubId ? [{ type: "Post" as const, id: `LIST-${clubId}` }] : []),
      ],
    }),

    // Close a poll (matches mobile's closePoll)
    closePoll: builder.mutation<{ success: boolean }, { pollId: string; postId: string }>({
      async queryFn({ pollId }) {
        try {
          const { error } = await supabase
            .from("polls")
            .update({ is_closed: true })
            .eq("id", pollId);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { postId }) => [
        { type: "Post", id: postId },
        { type: "Poll", id: postId },
      ],
    }),

    // Toggle post like (matches mobile's togglePostLike pattern)
    togglePostLike: builder.mutation<
      { liked: boolean },
      { postId: string; clubId: string; userId: string }
    >({
      async queryFn({ postId }) {
        try {
          const { data, error } = await supabase.rpc("toggle_post_like", {
            p_post_id: postId,
          });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: { liked: !!data } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { postId, clubId }) => [
        { type: "Post", id: postId },
        { type: "Post", id: `LIST-${clubId}` },
      ],
    }),

    // Toggle post pin (matches mobile's togglePostPin pattern)
    togglePostPin: builder.mutation<
      { pinned: boolean; pinned_date: string | null },
      { postId: string; clubId: string }
    >({
      async queryFn({ postId }) {
        try {
          const { data, error } = await supabase.rpc("toggle_post_pin", {
            p_post_id: postId,
          });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return {
            data: {
              pinned: !!data,
              pinned_date: data ? new Date().toISOString() : null,
            },
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { postId, clubId }) => [
        { type: "Post", id: postId },
        { type: "Post", id: `LIST-${clubId}` },
      ],
    }),

    // Toggle comment like (matches mobile's toggleCommentLike pattern)
    toggleCommentLike: builder.mutation<
      { liked: boolean },
      { commentId: string; postId: string; userId: string }
    >({
      async queryFn({ commentId }) {
        try {
          const { data, error } = await supabase.rpc("toggle_comment_like", {
            p_comment_id: commentId,
          });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: { liked: !!data } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { postId }) => [
        { type: "Comment", id: `LIST-${postId}` },
      ],
    }),

    // Delete a comment (matches mobile's deleteComment pattern)
    deleteComment: builder.mutation<
      { success: boolean },
      { commentId: string; postId: string }
    >({
      async queryFn({ commentId }) {
        try {
          const { error } = await supabase
            .from("comments")
            .delete()
            .eq("id", commentId);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { commentId, postId }) => [
        { type: "Comment", id: commentId },
        { type: "Comment", id: `LIST-${postId}` },
        { type: "Post", id: postId }, // Refresh post to update comment_count
      ],
    }),

    // Delete a post (matches mobile's deletePost pattern)
    deletePost: builder.mutation<{ success: boolean }, { postId: string; clubId: string }>({
      async queryFn({ postId }) {
        try {
          const { error } = await supabase.from("posts").delete().eq("id", postId);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { postId, clubId }) => [
        { type: "Post", id: postId },
        { type: "Post", id: `LIST-${clubId}` },
      ],
    }),

    // Update a post (matches mobile's updatePost pattern)
    updatePost: builder.mutation<
      Post,
      { postId: string; clubId: string; updates: Partial<Post> }
    >({
      async queryFn({ postId, updates }) {
        try {
          // Remove fields that shouldn't be updated directly
          const { id: _id, created_at: _created_at, updated_at: _updated_at, post_media: _post_media, liked: _liked, poll: _poll, ...safeUpdates } =
            updates as any;

          const { data, error } = await supabase
            .from("posts")
            .update(safeUpdates)
            .eq("id", postId)
            .select("*, post_media(*)")
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return {
            data: {
              ...data,
              post_media: data.post_media || [],
              liked: false,
            } as Post,
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { postId, clubId }) => [
        { type: "Post", id: postId },
        { type: "Post", id: `LIST-${clubId}` },
      ],
    }),

    // Create post media record (matches mobile's createPostMedia)
    createPostMedia: builder.mutation<
      PostMedia,
      {
        postId: string;
        clubId: string;
        kind: "image" | "video";
        imageSmall?: string | null;
        imageMedium?: string | null;
        imageFull?: string | null;
        videoUrl?: string | null;
        width?: number | null;
        height?: number | null;
        aspectRatio?: number | null;
        duration?: number | null;
        sortOrder?: number;
      }
    >({
      async queryFn({
        postId,
        kind,
        imageSmall,
        imageMedium,
        imageFull,
        videoUrl,
        width,
        height,
        aspectRatio,
        duration,
        sortOrder = 0,
      }) {
        try {
          const { data, error } = await supabase
            .from("post_media")
            .insert({
              post_id: postId,
              kind,
              image_small: imageSmall || null,
              image_medium: imageMedium || null,
              image_full: imageFull || null,
              video_url: videoUrl || null,
              width: width || null,
              height: height || null,
              aspect_ratio: aspectRatio || null,
              duration: duration || null,
              sort_order: sortOrder,
            })
            .select()
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as PostMedia };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { postId, clubId }) => [
        { type: "Post", id: postId },
        { type: "Post", id: `LIST-${clubId}` },
      ],
    }),

    // Fetch all topics with their categories
    fetchTopics: builder.query<Topic[], void>({
      async queryFn() {
        try {
          const { data, error } = await supabase
            .from("topics")
            .select("*, topic_categories(*)")
            .order("display_order", { ascending: true })
            .order("name", { ascending: true });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: (data || []) as Topic[] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: [{ type: "Topic" as const, id: "LIST" }],
    }),

    // Fetch all subtopics with their parent topics
    fetchSubtopics: builder.query<Subtopic[], void>({
      async queryFn() {
        try {
          const { data, error } = await supabase
            .from("subtopics")
            .select("*, parent_topic:topics(*)")
            .order("name", { ascending: true });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: (data || []) as Subtopic[] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: [{ type: "Subtopic" as const, id: "LIST" }],
    }),

    // Toggle club pin status (pin/unpin a club for the current user)
    toggleClubPin: builder.mutation<
      { pinned: boolean; pinned_at: string | null },
      { clubId: string; userId: string }
    >({
      async queryFn({ clubId, userId }) {
        try {
          // First check current pin status
          const { data: currentMember, error: fetchError } = await supabase
            .from("club_members")
            .select("id, pinned_at")
            .eq("club_id", clubId)
            .eq("user_id", userId)
            .single();

          if (fetchError) {
            return { error: { status: "CUSTOM_ERROR", error: fetchError.message } };
          }

          if (!currentMember) {
            return { error: { status: "CUSTOM_ERROR", error: "Club membership not found" } };
          }

          // Toggle pin status
          const isPinned = currentMember.pinned_at !== null;
          const newPinnedAt = isPinned ? null : new Date().toISOString();

          const { error: updateError } = await supabase
            .from("club_members")
            .update({ pinned_at: newPinnedAt })
            .eq("id", currentMember.id);

          if (updateError) {
            return { error: { status: "CUSTOM_ERROR", error: updateError.message } };
          }

          return { data: { pinned: !isPinned, pinned_at: newPinnedAt } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: [{ type: "ClubMember", id: "LIST" }],
    }),

    // Update user topics (interests)
    updateUserTopics: builder.mutation<
      { success: boolean },
      { userId: string; selectedIds: string[] }
    >({
      async queryFn({ userId, selectedIds }) {
        try {
          // 1. Delete existing user_topics
          const { error: deleteError } = await supabase
            .from("user_topics")
            .delete()
            .eq("user_id", userId);

          if (deleteError) {
            return { error: { status: "CUSTOM_ERROR", error: deleteError.message } };
          }

          // 2. Get all subtopic IDs to distinguish topics vs subtopics
          const { data: subtopics } = await supabase.from("subtopics").select("id");
          const subtopicIds = new Set(subtopics?.map((s) => s.id) || []);

          // 3. Build new records
          const newRecords = selectedIds.map((id) => ({
            user_id: userId,
            topic_id: subtopicIds.has(id) ? null : id,
            subtopic_id: subtopicIds.has(id) ? id : null,
          }));

          // 4. Insert new user_topics
          if (newRecords.length > 0) {
            const { error: insertError } = await supabase
              .from("user_topics")
              .insert(newRecords);

            if (insertError) {
              return { error: { status: "CUSTOM_ERROR", error: insertError.message } };
            }
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "UserProfile", id: userId },
      ],
    }),

    // Notifications Endpoints
    // -----------------------------------------------------
    fetchNotifications: builder.query<Notification[], { maxAgeDays?: number }>({
      async queryFn({ maxAgeDays = 30 }) {
        try {
          const cutoffDate = new Date();
          cutoffDate.setDate(cutoffDate.getDate() - maxAgeDays);

          const { data, error } = await supabase
            .from("notifications")
            .select("*")
            .gte("created_at", cutoffDate.toISOString())
            .order("created_at", { ascending: false });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data || [] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Notification" as const, id })),
              { type: "Notification", id: "LIST" },
            ]
          : [{ type: "Notification", id: "LIST" }],
    }),

    fetchUnreadNotificationsCount: builder.query<number, void>({
      async queryFn() {
        try {
          const { count, error } = await supabase
            .from("notifications")
            .select("id", { count: "exact", head: true })
            .eq("is_read", false);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: count || 0 };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: [{ type: "Notification", id: "COUNT" }],
    }),

    markNotificationAsRead: builder.mutation<void, string>({
      async queryFn(id) {
        try {
          const { error } = await supabase
            .from("notifications")
            .update({ is_read: true })
            .eq("id", id);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, id) => [
        { type: "Notification", id },
        { type: "Notification", id: "COUNT" },
      ],
    }),

    markAllNotificationsAsRead: builder.mutation<void, void>({
      async queryFn() {
        try {
          const { error } = await supabase
            .from("notifications")
            .update({ is_read: true })
            .eq("is_read", false);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: [
        { type: "Notification", id: "LIST" },
        { type: "Notification", id: "COUNT" },
      ],
    }),

    // -------------------------------------------------------
    // Payment Endpoints
    // -------------------------------------------------------

    // Fetch club payment settings (admin - full details including Stripe account info)
    fetchClubPaymentSettings: builder.query<ClubPaymentSettingsFull | null, string>({
      async queryFn(clubId) {
        try {
          const { data, error } = await supabase
            .from("club_payment_settings")
            .select("*")
            .eq("club_id", clubId)
            .single();

          if (error) {
            if (error.code === "PGRST116") {
              return { data: null };
            }
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as ClubPaymentSettingsFull };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, clubId) => [
        { type: "ClubPaymentSettings", id: clubId },
      ],
    }),

    // Fetch public club payment settings (for non-members viewing pricing)
    fetchPublicClubPaymentSettings: builder.query<ClubPaymentSettingsFull | null, string>({
      async queryFn(clubId) {
        try {
          const { data, error } = await supabase
            .from("club_payment_settings_public")
            .select("*")
            .eq("club_id", clubId)
            .single();

          if (error) {
            if (error.code === "PGRST116") {
              return { data: null };
            }
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as ClubPaymentSettingsFull };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, clubId) => [
        { type: "ClubPaymentSettings", id: `public-${clubId}` },
      ],
    }),

    // Fetch user's club subscription
    fetchClubSubscription: builder.query<
      UserClubSubscription | null,
      { clubId: string; userId: string }
    >({
      async queryFn({ clubId, userId }) {
        try {
          const { data, error } = await supabase
            .from("club_subscriptions")
            .select("*")
            .eq("club_id", clubId)
            .eq("user_id", userId)
            .eq("type", "membership")
            .single();

          if (error) {
            if (error.code === "PGRST116") {
              return { data: null };
            }
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as UserClubSubscription };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, { clubId, userId }) => [
        { type: "ClubSubscription", id: `${clubId}-${userId}` },
      ],
    }),

    // Fetch all clubs the user has an active subscription to
    fetchUserSubscribedClubs: builder.query<
      (Club & { subscription: import("@tinyclub/shared/models").UserClubSubscription })[],
      string
    >({
      async queryFn(userId) {
        try {
          const { data, error } = await supabase
            .from("club_subscriptions")
            .select("*, clubs(*)")
            .eq("user_id", userId)
            .in("status", ["active", "trialing"])
            .eq("type", "membership");

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          // Deduplicate by club_id (user may have multiple subscription records)
          const uniqueClubs = new Map<
            string,
            Club & { subscription: import("@tinyclub/shared/models").UserClubSubscription }
          >();
          (data || []).forEach((sub: any) => {
            if (sub.clubs && !uniqueClubs.has(sub.club_id)) {
              const { clubs, ...subscription } = sub;
              uniqueClubs.set(sub.club_id, { ...clubs, subscription });
            }
          });

          return { data: Array.from(uniqueClubs.values()) };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, userId) => [
        { type: "ClubSubscription", id: `USER-SUBSCRIBED-${userId}` },
      ],
    }),

    // Fetch public club data by invite code (no auth needed)
    fetchPublicClubByInviteCode: builder.query<ClubPublicData | null, string>({
      async queryFn(inviteCode) {
        try {
          const { data, error } = await supabase
            .from("club_public_data")
            .select("*")
            .eq("invite_code", inviteCode)
            .single();

          if (error) {
            if (error.code === "PGRST116") {
              return { data: null };
            }
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as ClubPublicData };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, inviteCode) => [
        { type: "Club", id: `invite-${inviteCode}` },
      ],
    }),

    // Create a club member (for open/free clubs - direct join)
    createClubMember: builder.mutation<
      ClubMember,
      { clubId: string; userId: string }
    >({
      async queryFn({ clubId, userId }) {
        try {
          const { data, error } = await supabase
            .from("club_members")
            .insert({
              club_id: clubId,
              user_id: userId,
              role: "member",
            })
            .select("*")
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as ClubMember };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { clubId }) => [
        { type: "ClubMember", id: "LIST" },
        { type: "ClubMember", id: `LIST-${clubId}` },
        { type: "Club", id: clubId },
      ],
    }),

    // Delete a club member (leave club)
    deleteClubMember: builder.mutation<
      { success: boolean },
      { clubId: string; userId: string }
    >({
      async queryFn({ clubId, userId }) {
        try {
          const { error } = await supabase
            .from("club_members")
            .delete()
            .eq("club_id", clubId)
            .eq("user_id", userId);

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { clubId, userId }) => [
        { type: "ClubMember", id: "LIST" },
        { type: "ClubMember", id: `LIST-${clubId}` },
        { type: "ClubMember", id: `${clubId}-${userId}` },
        { type: "Club", id: clubId },
      ],
    }),

    // Request to join a club (for private/request-only clubs)
    requestToJoinClub: builder.mutation<
      { success: boolean },
      { clubId: string; userId: string }
    >({
      async queryFn({ clubId, userId }) {
        try {
          const { error } = await supabase.rpc("request_to_join_club", {
            p_club_id: clubId,
            p_user_id: userId,
          });

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { clubId }) => [
        { type: "Club", id: clubId },
        { type: "ClubMember", id: "LIST" },
        { type: "ClubMember", id: `LIST-${clubId}` },
      ],
    }),

    // Fetch a single event by occurrence ID (no auth required - public access)
    // Mirrors mobile's fetchEvent pattern (mobile line 1092)
    fetchEventById: builder.query<EventOccurrence | null, string>({
      async queryFn(eventId) {
        try {
          const { data, error } = await supabase
            .from("event_occurrences")
            .select("*, rootEvent:events!inner(*), event_attendees(*), event_reactions(*)")
            .eq("id", eventId)
            .eq("canceled", false)
            .single();

          if (error) {
            if (error.code === "PGRST116") {
              return { data: null };
            }
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return {
            data: {
              ...data,
              rootEvent: data.rootEvent,
              event_attendees: data.event_attendees || [],
              event_reactions: data.event_reactions || [],
            } as EventOccurrence,
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, id) => [{ type: "EventOccurrence", id }],
    }),

    // Fetch a single event occurrence by invite code (no auth required - public access)
    fetchEventByInviteCode: builder.query<EventOccurrence | null, string>({
      async queryFn(inviteCode) {
        try {
          const { data, error } = await supabase
            .from("event_occurrences")
            .select("*, rootEvent:events!inner(*), event_attendees(*), event_reactions(*)")
            .eq("invite_code", inviteCode)
            .eq("canceled", false)
            .single();

          if (error) {
            if (error.code === "PGRST116") {
              return { data: null };
            }
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return {
            data: {
              ...data,
              rootEvent: data.rootEvent,
              event_attendees: data.event_attendees || [],
              event_reactions: data.event_reactions || [],
            } as EventOccurrence,
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (result) => result ? [{ type: "EventOccurrence", id: result.id }] : [],
    }),

    // Update club payment settings (admin)
    updateClubPaymentSettings: builder.mutation<
      ClubPaymentSettingsFull | null,
      Partial<ClubPaymentSettingsFull> & { club_id: string }
    >({
      async queryFn(settings) {
        try {
          const { data, error } = await supabase
            .from("club_payment_settings")
            .upsert(settings)
            .select("*")
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR", error: error.message } };
          }

          return { data: data as ClubPaymentSettingsFull };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { club_id }) => [
        { type: "ClubPaymentSettings", id: club_id },
        { type: "ClubPaymentSettings", id: `public-${club_id}` },
      ],
    }),

    // Fetch club member count (matches mobile's fetchClubMemberCount)
    fetchClubMemberCount: builder.query<number, { clubId: string }>({
      async queryFn({ clubId }) {
        try {
          const { count, error } = await (supabase
            .from("club_members")
            .select("user_id, user_profiles!inner(is_active)", { count: "exact", head: true }) as any)
            .eq("club_id", clubId)
            .eq("user_profiles.is_active", true);

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: (error as any).message } };
          }

          return { data: (count as number) || 0 };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, { clubId }) => [{ type: "ClubMember", id: `COUNT-${clubId}` }],
    }),

    // Fetch club email quota (matches mobile's fetchClubEmailQuota)
    fetchClubEmailQuota: builder.query<
      { used: number; limit: number; remaining: number },
      string
    >({
      async queryFn(clubId) {
        try {
          const now = new Date();
          const monthYear = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}`;

          // Get club owner
          const { data: ownerMembership, error: ownerError } = await supabase
            .from("club_members")
            .select("user_id")
            .eq("club_id", clubId)
            .eq("role", "owner")
            .order("joined_at", { ascending: true })
            .limit(1)
            .single();

          if (ownerError || !ownerMembership) {
            throw new Error("Club owner not found");
          }

          // Get owner's membership tier
          const { data: ownerProfile, error: profileError } = await supabase
            .from("user_profiles")
            .select("membership_type")
            .eq("id", ownerMembership.user_id)
            .single();

          if (profileError) {
            throw new Error(`Failed to fetch owner profile: ${profileError.message}`);
          }

          const ownerTier = (ownerProfile?.membership_type || "free") as string;

          // Get tier limits
          const { data: tierLimits, error: tierError } = await supabase
            .from("membership_tier_limits")
            .select("max_email_sends_per_month")
            .eq("tier_name", ownerTier)
            .single();

          if (tierError || !tierLimits) {
            throw new Error(`Failed to fetch tier limits: ${tierError?.message || "Not found"}`);
          }

          const limit = tierLimits.max_email_sends_per_month || 15;

          // Count current month's sends
          const { count, error: countError } = await supabase
            .from("club_email_sends")
            .select("*", { count: "exact", head: true })
            .eq("club_id", clubId)
            .eq("month_year", monthYear);

          if (countError) {
            throw new Error(`Failed to count email sends: ${countError.message}`);
          }

          const used = count || 0;
          const remaining = Math.max(0, limit - used);

          return { data: { used, limit, remaining } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, clubId) => [{ type: "ClubEmailQuota", id: clubId }],
    }),

    // Fetch banned words for content validation (matches mobile's fetchBannedWordsQuery)
    fetchBannedWords: builder.query<string[], void>({
      async queryFn() {
        try {
          const { data, error } = await supabase
            .from("banned_words")
            .select("word")
            .order("word", { ascending: true });

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: (data || []).map((item: { word: string }) => item.word) };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: [{ type: "BannedWord", id: "LIST" }],
    }),

    // Send post email to club members (matches mobile's sendPostEmail mutation)
    sendPostEmail: builder.mutation<
      { success: boolean; recipientCount: number },
      { clubId: string; postId: string }
    >({
      async queryFn({ clubId, postId }) {
        try {
          const { data: { session } } = await supabase.auth.getSession();

          if (!session?.access_token) {
            return { error: { status: "CUSTOM_ERROR" as const, error: "Not authenticated" } };
          }

          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

          const response = await fetch(
            `${supabaseUrl}/functions/v1/send-post-email`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${session.access_token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ clubId, postId, timezone }),
            }
          );

          if (!response.ok) {
            const text = await response.text();
            return { error: { status: "CUSTOM_ERROR" as const, error: text || "Failed to send email" } };
          }

          const result = await response.json();
          return { data: { success: true, recipientCount: result.recipientCount ?? 0 } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { clubId }) => [{ type: "ClubEmailQuota", id: clubId }],
    }),

    // ── Post Likes ────────────────────────────────────────────────────

    fetchPostLikes: builder.query<
      Array<{ id: string; post_id: string; user_id: string; created_at: string }>,
      { postId: string }
    >({
      async queryFn({ postId }) {
        try {
          const { data, error } = await supabase
            .from("post_likes")
            .select("*")
            .eq("post_id", postId)
            .order("liked_at", { ascending: false });

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          // Map liked_at to created_at and add composite id (matches mobile)
          const mapped = (data || []).map((like) => ({
            ...like,
            id: `${like.post_id}-${like.user_id}`,
            created_at: like.liked_at,
          }));

          return { data: mapped };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, { postId }) => [
        { type: "Post", id: `LIKES-${postId}` },
      ],
    }),

    // ── Blocked Users List ─────────────────────────────────────────────

    fetchBlockedUsers: builder.query<UserBlock[], string>({
      async queryFn(userId) {
        try {
          const { data, error } = await supabase
            .from("user_blocks")
            .select("*")
            .eq("blocker_id", userId);

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: (data || []) as UserBlock[] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, userId) => [
        { type: "UserBlock", id: `LIST-${userId}` },
      ],
    }),

    // ── User Blocking ──────────────────────────────────────────────────

    checkBlockStatus: builder.query<
      { iBlockedThem: boolean; theyBlockedMe: boolean },
      { userId: string; otherUserId: string }
    >({
      async queryFn({ userId, otherUserId }) {
        try {
          const { data, error } = await supabase
            .from("user_blocks")
            .select("blocker_id, blocked_id")
            .or(
              `and(blocker_id.eq.${userId},blocked_id.eq.${otherUserId}),and(blocker_id.eq.${otherUserId},blocked_id.eq.${userId})`
            );

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          const rows = data || [];
          return {
            data: {
              iBlockedThem: rows.some((b) => b.blocker_id === userId && b.blocked_id === otherUserId),
              theyBlockedMe: rows.some((b) => b.blocker_id === otherUserId && b.blocked_id === userId),
            },
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, { userId, otherUserId }) => [
        { type: "UserBlock", id: `${userId}-${otherUserId}` },
      ],
    }),

    blockUser: builder.mutation<UserBlock | null, { blockerId: string; blockedId: string }>({
      async queryFn({ blockerId, blockedId }) {
        try {
          const { data, error } = await supabase
            .from("user_blocks")
            .insert({ blocker_id: blockerId, blocked_id: blockedId })
            .select("*")
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: data as UserBlock };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { blockerId, blockedId }) => [
        { type: "UserBlock", id: `${blockerId}-${blockedId}` },
        { type: "UserBlock", id: `LIST-${blockerId}` },
        { type: "Post", id: "LIST" },
        { type: "Comment", id: "LIST" },
      ],
    }),

    unblockUser: builder.mutation<{ success: boolean }, { blockerId: string; blockedId: string }>({
      async queryFn({ blockerId, blockedId }) {
        try {
          const { error } = await supabase
            .from("user_blocks")
            .delete()
            .eq("blocker_id", blockerId)
            .eq("blocked_id", blockedId);

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { blockerId, blockedId }) => [
        { type: "UserBlock", id: `${blockerId}-${blockedId}` },
        { type: "UserBlock", id: `LIST-${blockerId}` },
        { type: "Post", id: "LIST" },
        { type: "Comment", id: "LIST" },
      ],
    }),

    // ── Moderation ──────────────────────────────────────────────────────

    // Check if the current user is a global admin (in admin_users table)
    checkIsAdmin: builder.query<boolean, string>({
      async queryFn(userId) {
        try {
          const { data, error } = await supabase
            .from("admin_users")
            .select("id")
            .eq("user_id", userId);

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: Array.isArray(data) && data.length > 0 };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
    }),

    // Fetch moderation reports (admin only - RLS enforces access)
    fetchModerationReports: builder.query<
      ModerationReport[],
      { processed?: boolean }
    >({
      async queryFn({ processed = false }) {
        try {
          const query = supabase
            .from("moderation_reports")
            .select(
              `*, reporter:user_profiles!reporter_id(id, first_name, last_name, username, avatar_small)`
            )
            .eq("processed", processed)
            .order("created_at", { ascending: false });

          const { data, error } = await query;

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: (data || []) as ModerationReport[] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({
                type: "ModerationReport" as const,
                id: r.id,
              })),
              { type: "ModerationReport", id: "LIST" },
            ]
          : [{ type: "ModerationReport", id: "LIST" }],
    }),

    // Fetch a reported item's content for inline preview
    fetchReportedItem: builder.query<
      { type: string; content: string; author?: string; clubName?: string; clubId?: string },
      { itemType: string; itemId: string }
    >({
      async queryFn({ itemType, itemId }) {
        try {
          if (itemType === "post") {
            const { data, error } = await supabase
              .from("posts")
              .select("content, club_id, user_id, clubs(name), user_profiles!user_id(first_name, last_name, username)")
              .eq("id", itemId)
              .single();
            if (error) return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
            const profile = data.user_profiles as any;
            const author = profile?.first_name
              ? `${profile.first_name}${profile.last_name ? ` ${profile.last_name}` : ""}`
              : profile?.username || "Unknown";
            return {
              data: {
                type: "post",
                content: data.content || "",
                author,
                clubName: (data.clubs as any)?.name || "",
                clubId: data.club_id,
              },
            };
          }

          if (itemType === "comment") {
            const { data, error } = await supabase
              .from("comments")
              .select("content, post_id, user_id, user_profiles!user_id(first_name, last_name, username), posts!post_id(club_id, clubs(name))")
              .eq("id", itemId)
              .single();
            if (error) return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
            const profile = data.user_profiles as any;
            const author = profile?.first_name
              ? `${profile.first_name}${profile.last_name ? ` ${profile.last_name}` : ""}`
              : profile?.username || "Unknown";
            const post = data.posts as any;
            return {
              data: {
                type: "comment",
                content: data.content || "",
                author,
                clubName: post?.clubs?.name || "",
                clubId: post?.club_id,
              },
            };
          }

          if (itemType === "club") {
            const { data, error } = await supabase
              .from("clubs")
              .select("id, name, description")
              .eq("id", itemId)
              .single();
            if (error) return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
            return {
              data: {
                type: "club",
                content: data.description || data.name || "",
                clubName: data.name || "",
                clubId: data.id,
              },
            };
          }

          if (itemType === "user") {
            const { data, error } = await supabase
              .from("user_profiles")
              .select("id, first_name, last_name, username, bio")
              .eq("id", itemId)
              .single();
            if (error) return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
            const name = data.first_name
              ? `${data.first_name}${data.last_name ? ` ${data.last_name}` : ""}`
              : data.username || "Unknown";
            return {
              data: {
                type: "user",
                content: data.bio || "",
                author: name,
              },
            };
          }

          return { error: { status: "CUSTOM_ERROR" as const, error: `Unknown item type: ${itemType}` } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
    }),

    // Execute a moderation action (approve/reject) via edge function
    moderateAction: builder.mutation<
      { success: boolean },
      { reportId: number; itemType: string; itemId: string; action: "approve" | "reject" }
    >({
      async queryFn({ reportId, itemType, itemId, action }) {
        try {
          const { error } = await supabase.functions.invoke("moderate_action", {
            body: { reportId, itemType, itemId, action },
          });

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { itemType }) => {
        const tags: Array<{ type: "ModerationReport" | "Post" | "Comment" | "Club" | "UserProfile"; id: string }> = [
          { type: "ModerationReport", id: "LIST" },
        ];
        if (itemType === "post") tags.push({ type: "Post", id: "LIST" });
        if (itemType === "comment") {
          tags.push({ type: "Comment", id: "LIST" });
          tags.push({ type: "Post", id: "LIST" });
        }
        if (itemType === "club") tags.push({ type: "Club", id: "LIST" });
        if (itemType === "user") tags.push({ type: "UserProfile", id: "LIST" });
        return tags;
      },
    }),

    // ── Notification Preferences ────────────────────────────────────────

    // Update per-club notification preferences via RPC
    // Club info (links & files)
    fetchClubInfo: builder.query<ClubInfoData, string>({
      async queryFn(clubId) {
        try {
          const { data, error } = await supabase
            .from("clubs")
            .select("club_links(*),club_files(*)")
            .eq("id", clubId)
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return {
            data: {
              links: (data as any)?.club_links || [],
              files: (data as any)?.club_files || [],
              media: [],
            },
          };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, clubId) => [
        { type: "ClubInfo" as const, id: clubId },
      ],
    }),

    createClubLink: builder.mutation<
      ClubLink,
      { club_id: string; name?: string | null; url: string; creator_id: string }
    >({
      async queryFn(args) {
        try {
          const { data, error } = await supabase
            .from("club_links")
            .insert({
              club_id: args.club_id,
              name: args.name || null,
              url: args.url,
              creator_id: args.creator_id,
            })
            .select()
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: data as ClubLink };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { club_id }) => [
        { type: "ClubInfo" as const, id: club_id },
      ],
    }),

    deleteClubLink: builder.mutation<void, { id: string; club_id: string }>({
      async queryFn({ id }) {
        try {
          const { error } = await supabase
            .from("club_links")
            .delete()
            .eq("id", id);

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { club_id }) => [
        { type: "ClubInfo" as const, id: club_id },
      ],
    }),

    createClubFile: builder.mutation<
      ClubFile,
      {
        club_id: string;
        name: string;
        file_url: string;
        filename: string;
        mime_type: string;
        creator_id: string;
      }
    >({
      async queryFn(args) {
        try {
          const { data, error } = await supabase
            .from("club_files")
            .insert({
              club_id: args.club_id,
              name: args.name,
              file_url: args.file_url,
              filename: args.filename,
              mime_type: args.mime_type,
              creator_id: args.creator_id,
            })
            .select()
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: data as ClubFile };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { club_id }) => [
        { type: "ClubInfo" as const, id: club_id },
      ],
    }),

    deleteClubFile: builder.mutation<void, { id: string; club_id: string }>({
      async queryFn({ id }) {
        try {
          const { error } = await supabase
            .from("club_files")
            .delete()
            .eq("id", id);

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: undefined };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { club_id }) => [
        { type: "ClubInfo" as const, id: club_id },
      ],
    }),

    updateNotificationPreferences: builder.mutation<
      unknown,
      {
        userId: string;
        clubId: string;
        preferences: {
          posts?: "everything" | "comments_likes_only" | "nothing";
          events?: "everything" | "reminders_only" | "nothing";
        };
      }
    >({
      async queryFn({ userId, clubId, preferences }) {
        try {
          const { error } = await supabase.rpc(
            "update_notification_preferences",
            {
              p_user_id: userId,
              p_club_id: clubId,
              p_preferences: preferences,
            }
          );

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: { success: true } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { userId }) => [
        { type: "UserProfile" as const, id: userId },
      ],
    }),

    // --- Club Invitations ---

    importMembers: builder.mutation<
      {
        success: boolean;
        results: Array<{
          name: string;
          email?: string;
          status: "added_directly" | "already_member" | "invited" | "saved_without_email" | "error";
          error?: string;
        }>;
        summary: {
          addedDirectly: number;
          alreadyMembers: number;
          invited: number;
          savedWithoutEmail: number;
          errors: number;
          emailsSentImmediately: number;
          emailsQueued: number;
        };
      },
      {
        clubId: string;
        members: Array<{
          name: string;
          firstName?: string;
          lastName?: string;
          email?: string;
          role: "member" | "cohost";
          sourceMetadata?: Record<string, unknown>;
        }>;
        source: "meetup_import" | "screenshot_import" | "manual";
      }
    >({
      async queryFn(params) {
        try {
          const { data, error } = await supabase.functions.invoke(
            "import-meetup-members",
            { body: params }
          );

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { clubId }) => [
        { type: "ClubMember" as const, id: "LIST" },
        { type: "ClubInvitation" as const, id: "LIST" },
        { type: "Club" as const, id: clubId },
      ],
    }),

    acceptClubInvitation: builder.mutation<
      {
        success: boolean;
        club?: { id: string; name: string | null };
        alreadyMember?: boolean;
      },
      { inviteToken: string }
    >({
      async queryFn(params) {
        try {
          const { data, error } = await supabase.functions.invoke(
            "accept-club-invitation",
            { body: params }
          );

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: [
        { type: "ClubMember" as const, id: "LIST" },
        { type: "Club" as const, id: "LIST" },
      ],
    }),

    fetchClubInvitations: builder.query<ClubInvitation[], { clubId: string; status?: string }>({
      async queryFn({ clubId, status }) {
        try {
          let query = supabase
            .from("club_invitations")
            .select("*")
            .eq("club_id", clubId)
            .order("created_at", { ascending: false });

          if (status && status !== "all") {
            query = query.eq("status", status);
          }

          const { data, error } = await query;

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: data || [] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((inv) => ({
                type: "ClubInvitation" as const,
                id: inv.id,
              })),
              { type: "ClubInvitation" as const, id: "LIST" },
            ]
          : [{ type: "ClubInvitation" as const, id: "LIST" }],
    }),

    updateClubInvitation: builder.mutation<
      ClubInvitation,
      { id: string; updates: Partial<Pick<ClubInvitation, "email" | "first_name" | "last_name" | "status">> }
    >({
      async queryFn({ id, updates }) {
        try {
          const { data, error } = await supabase
            .from("club_invitations")
            .update(updates)
            .eq("id", id)
            .select()
            .single();

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ClubInvitation" as const, id },
        { type: "ClubInvitation" as const, id: "LIST" },
      ],
    }),

    sendClubInvitations: builder.mutation<
      { success: boolean; sent: number; failed: number; errors: string[] },
      { clubId: string; invitationIds: string[] }
    >({
      async queryFn(params) {
        try {
          const { data, error } = await supabase.functions.invoke(
            "send-club-invitations",
            { body: params }
          );

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: [
        { type: "ClubInvitation" as const, id: "LIST" },
      ],
    }),

    // Club data export jobs — the 5 most recent requests made by the current user
    // for one scope. RLS on club_export_jobs already restricts rows to
    // requested_by = auth.uid(). The scope filter keeps the two entry points
    // apart: "Export club data" and "Export my data" produce very different
    // archives, so a whole-club zip must never show up in the personal list.
    fetchClubExportJobs: builder.query<
      ClubExportJob[],
      { clubId: string; scope: ClubExportScope }
    >({
      async queryFn({ clubId, scope }) {
        try {
          // Named columns, not "*": the row also carries the export's internal
          // checkpoint (state, lease_token, hops), which is megabytes of zip
          // and upload bookkeeping and would be re-fetched by every 5s poll.
          const { data, error } = await supabase
            .from("club_export_jobs")
            .select(
              "id, club_id, requested_by, scope, include_media, status, progress, error_message, storage_key, download_url, file_size_bytes, expires_at, created_at, updated_at, completed_at",
            )
            .eq("club_id", clubId)
            .eq("scope", scope)
            .order("created_at", { ascending: false })
            .limit(5);

          if (error) {
            return { error: { status: "CUSTOM_ERROR" as const, error: error.message } };
          }

          return { data: (data || []) as ClubExportJob[] };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      providesTags: (_result, _error, { clubId, scope }) => [
        { type: "ClubExportJob" as const, id: `${clubId}:${scope}` },
      ],
    }),

    // Request a club data export. The edge function authorizes, queues a job and
    // responds 202 immediately; the zip is built in the background.
    requestClubExport: builder.mutation<
      { jobId: string; status: string },
      { clubId: string; scope: ClubExportScope; includeMedia: boolean }
    >({
      async queryFn({ clubId, scope, includeMedia }) {
        try {
          const { data, error } = await supabase.functions.invoke(
            "export-club-data",
            { body: { clubId, scope, includeMedia } }
          );

          if (error) {
            // For non-2xx responses, the server's error message is in the response body
            let message = error.message;
            try {
              const body = await (error as { context?: Response }).context?.json();
              if (body?.error) message = body.error;
            } catch {
              // fall back to the generic invoke error message
            }
            return { error: { status: "CUSTOM_ERROR" as const, error: message } };
          }

          return { data: data as { jobId: string; status: string } };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error instanceof Error ? error.message : "An error occurred",
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { clubId, scope }) => [
        { type: "ClubExportJob" as const, id: `${clubId}:${scope}` },
      ],
    }),
  }),
});

export const {
  useFetchClubsQuery,
  useGetFeaturedClubsQuery,
  useGetRecommendedClubsQuery,
  useFetchAllClubMembersQuery,
  useGetClubQuery,
  useCheckClubMembershipQuery,
  useFetchClubPostsQuery,
  useFetchUserProfileQuery,
  useFetchCurrentUserProfileQuery,
  useFetchUserProfilesByIdsQuery,
  useUpdateUserProfileMutation,
  useFetchClubMembersQuery,
  useFetchAvailableClubMembersQuery,
  useFetchUserDiscoverableClubsQuery,
  useFetchClubEventsQuery,
  useFetchAllEventsQuery,
  useFetchPostQuery,
  useFetchPostCommentsQuery,
  useCreateCommentMutation,
  useCreatePostMutation,
  useCreateEventAttendeeMutation,
  useUpdateEventAttendeeMutation,
  useDeleteEventAttendeeMutation,
  useCreateClubMutation,
  useUpdateClubMutation,
  useDeleteClubMutation,
  useCreateEventMutation,
  useCreateEventOccurrenceMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
  // Event reactions
  useCreateEventReactionMutation,
  useDeleteEventReactionMutation,
  // Poll mutations
  useCreatePollMutation,
  useDeletePollMutation,
  useVotePollMutation,
  useClosePollMutation,
  // Post mutations
  useTogglePostLikeMutation,
  useTogglePostPinMutation,
  useToggleCommentLikeMutation,
  useDeleteCommentMutation,
  useDeletePostMutation,
  useUpdatePostMutation,
  useCreatePostMediaMutation,
  // Topics
  useFetchTopicsQuery,
  useFetchSubtopicsQuery,
  useUpdateUserTopicsMutation,
  // Club pinning
  useToggleClubPinMutation,
  // Notifications
  useFetchNotificationsQuery,
  useFetchUnreadNotificationsCountQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  // Public club data
  useFetchPublicClubByInviteCodeQuery,
  // Public event detail
  useFetchEventByIdQuery,
  useFetchEventByInviteCodeQuery,
  // Join/leave actions
  useCreateClubMemberMutation,
  useDeleteClubMemberMutation,
  useRequestToJoinClubMutation,
  // Payment
  useFetchClubPaymentSettingsQuery,
  useFetchPublicClubPaymentSettingsQuery,
  useFetchClubSubscriptionQuery,
  useFetchUserSubscribedClubsQuery,
  useUpdateClubPaymentSettingsMutation,
  // Mention autocomplete
  useSearchClubMembersForMentionQuery,
  useLazySearchClubMembersForMentionQuery,
  // Content validation
  useFetchBannedWordsQuery,
  // Email post
  useFetchClubMemberCountQuery,
  useFetchClubEmailQuotaQuery,
  useSendPostEmailMutation,
  // Post likes
  useFetchPostLikesQuery,
  // Blocked users list
  useFetchBlockedUsersQuery,
  // User blocking
  useCheckBlockStatusQuery,
  useBlockUserMutation,
  useUnblockUserMutation,
  // Moderation
  useCheckIsAdminQuery,
  useFetchModerationReportsQuery,
  useFetchReportedItemQuery,
  useModerateActionMutation,
  // Notification preferences
  useUpdateNotificationPreferencesMutation,
  // Club info (links & files)
  useFetchClubInfoQuery,
  useCreateClubLinkMutation,
  useDeleteClubLinkMutation,
  useCreateClubFileMutation,
  useDeleteClubFileMutation,
  // Club invitations
  useImportMembersMutation,
  useAcceptClubInvitationMutation,
  useFetchClubInvitationsQuery,
  useUpdateClubInvitationMutation,
  useSendClubInvitationsMutation,
  // Club data export
  useFetchClubExportJobsQuery,
  useRequestClubExportMutation,
} = clubsApi;
